import type {
  BreakdownTableIndex,
  BreakdownCell,
} from '@/components/common/breakdown-tables/types';
import {
  getCell,
  hasSubDimensions,
  getSubDimensions,
  extractNumericValue,
} from '@/components/common/breakdown-tables/indexing';

export interface AggregateForScopeParams {
  index: BreakdownTableIndex;
  row?: string;
  subRow?: string | null;
  column?: string;
  subColumn?: string | null;
  expandedRows: Set<string>;
  expandedColumns: Set<string>;
  fillMissingSubRows?: boolean;
  fillMissingSubColumns?: boolean;
  subRowHasData: (row: string, subRow: string) => boolean;
  subColumnHasData: (column: string, subColumn: string) => boolean;
  xDim2Order?: string[];
  yDim2Order?: string[];
}

interface AxisResolutionParams {
  axis: 'row' | 'column';
  primary?: string;
  sub?: string | null;
  index: BreakdownTableIndex;
  expandedSet: Set<string>;
  fillMissing: boolean;
  subDimHasData: (primary: string, sub: string) => boolean;
  subDimOrder?: string[];
  otherAxisPrimary?: string;
  otherAxisSub?: string | null;
}

/**
 * Helper: Resolve a single collapsed dimension (with or without parent cell)
 * Used by both CASE C and CASE D
 */
function resolveCollapsedDimension(params: {
  axis: 'row' | 'column';
  primary: string;
  index: BreakdownTableIndex;
  fillMissing: boolean;
  subDimHasData: (primary: string, sub: string) => boolean;
  subDimOrder?: string[];
  otherAxisPrimary?: string;
  otherAxisSub?: string | null;
}): Array<{ primary: string; sub: string | null; isCollapsedGroup?: boolean }> {
  const {
    axis,
    primary,
    index,
    fillMissing,
    subDimHasData,
    subDimOrder,
    otherAxisPrimary,
    otherAxisSub,
  } = params;

  const results: Array<{
    primary: string;
    sub: string | null;
    isCollapsedGroup?: boolean;
  }> = [];

  const hasSub = hasSubDimensions(index, axis, primary);

  // Check if parent cell exists
  const canProbeParent =
    otherAxisPrimary !== undefined && otherAxisPrimary !== null;
  const parentCell =
    canProbeParent &&
    otherAxisPrimary !== undefined &&
    getCell(
      index,
      axis === 'column' ? primary : otherAxisPrimary,
      axis === 'column' ? null : (otherAxisSub ?? null),
      axis === 'row' ? primary : otherAxisPrimary,
      axis === 'row' ? null : (otherAxisSub ?? null),
    );

  // Use parent if it exists; otherwise aggregate subs (collapsed case)
  if (parentCell && parentCell.value != null) {
    results.push({ primary, sub: null });
  } else if (hasSub) {
    const subDims = getSubDimensions(index, axis, primary, {
      subColumnOrder: axis === 'column' ? subDimOrder : undefined,
      subRowOrder: axis === 'row' ? subDimOrder : undefined,
    });
    for (const s of subDims) {
      if (s === null) continue;
      if (!fillMissing && !subDimHasData(primary, s)) continue;
      results.push({ primary, sub: s });
    }
    // Mark this primary as a collapsed group for aggregation awareness
    results.push({ primary, sub: null, isCollapsedGroup: true });
  } else {
    results.push({ primary, sub: null });
  }

  return results;
}

/**
 * Resolve which primary/sub pairs to query for a given axis.
 * Explicitly differentiates between:
 * - No subdimension
 * - Expanded group
 * - Collapsed group (aggregates subs invisibly)
 * - Global total (no primary)
 */
export function resolveAxisDimensions(
  params: AxisResolutionParams,
): Array<{ primary: string; sub: string | null; isCollapsedGroup?: boolean }> {
  const {
    axis,
    primary,
    sub,
    index,
    expandedSet,
    fillMissing,
    subDimHasData,
    subDimOrder,
    otherAxisPrimary,
    otherAxisSub,
  } = params;

  const results: Array<{
    primary: string;
    sub: string | null;
    isCollapsedGroup?: boolean;
  }> = [];
  const allPrimaries = axis === 'column' ? index.columnValues : index.rowValues;

  // 🧠 CASE D: no primary at all → global total (all primaries)
  if (primary === undefined) {
    for (const prim of allPrimaries) {
      const hasSub = hasSubDimensions(index, axis, prim);
      const isExpanded = expandedSet.has(prim);

      if (hasSub && isExpanded) {
        // Expanded: add all sub-dimensions
        const subDims = getSubDimensions(index, axis, prim, {
          subColumnOrder: axis === 'column' ? subDimOrder : undefined,
          subRowOrder: axis === 'row' ? subDimOrder : undefined,
        }).filter(
          (s): s is string =>
            s !== null && (fillMissing || subDimHasData(prim, s)),
        );
        subDims.forEach((s) => results.push({ primary: prim, sub: s }));
      } else {
        // Collapsed or no sub-dimensions: use helper
        const resolved = resolveCollapsedDimension({
          axis,
          primary: prim,
          index,
          fillMissing,
          subDimHasData,
          subDimOrder,
          otherAxisPrimary,
          otherAxisSub,
        });
        results.push(...resolved);
      }
    }
    return results;
  }

  const hasSub = hasSubDimensions(index, axis, primary);
  const isExpanded = expandedSet.has(primary);

  // 🧠 CASE A: specific subdimension requested (not just "collapsed")
  // Note: sub=null means "aggregate across subs", not "find cells with sub=null"
  if (sub !== undefined && sub !== null) {
    results.push({ primary, sub });
    return results;
  }

  // 🧠 CASE B: expanded group
  if (hasSub && isExpanded) {
    const subDims = getSubDimensions(index, axis, primary, {
      subColumnOrder: axis === 'column' ? subDimOrder : undefined,
      subRowOrder: axis === 'row' ? subDimOrder : undefined,
    });
    for (const s of subDims) {
      if (s === null) continue;
      if (!fillMissing && !subDimHasData(primary, s)) continue;
      results.push({ primary, sub: s });
    }
    return results;
  }

  // 🧠 CASE C: collapsed group or total (aggregate across subdimensions invisibly)
  return resolveCollapsedDimension({
    axis,
    primary,
    index,
    fillMissing,
    subDimHasData,
    subDimOrder,
    otherAxisPrimary,
    otherAxisSub,
  });
}

/**
 * Collect all cell values for a given scope, respecting expansion and collapsed groups.
 */
export function getValuesForScope(
  params: AggregateForScopeParams,
): BreakdownCell['value'][] {
  const {
    index,
    row,
    subRow,
    column,
    subColumn,
    expandedRows,
    expandedColumns,
    fillMissingSubRows = false,
    fillMissingSubColumns = false,
    subRowHasData,
    subColumnHasData,
    xDim2Order,
    yDim2Order,
  } = params;

  const values: BreakdownCell['value'][] = [];

  // Resolve both axes independently
  const rowsToQuery = resolveAxisDimensions({
    axis: 'row',
    primary: row,
    sub: subRow,
    index,
    expandedSet: expandedRows,
    fillMissing: fillMissingSubRows,
    subDimHasData: subRowHasData,
    subDimOrder: yDim2Order,
    otherAxisPrimary: column,
    otherAxisSub: subColumn,
  });

  const columnsToQuery = resolveAxisDimensions({
    axis: 'column',
    primary: column,
    sub: subColumn,
    index,
    expandedSet: expandedColumns,
    fillMissing: fillMissingSubColumns,
    subDimHasData: subColumnHasData,
    subDimOrder: xDim2Order,
    otherAxisPrimary: row,
    otherAxisSub: subRow,
  });

  // Combine via cross-product
  rowsToQuery.forEach(
    ({ primary: rowPrimary, sub: rowSub, isCollapsedGroup: rowCollapsed }) => {
      columnsToQuery.forEach(
        ({
          primary: colPrimary,
          sub: colSub,
          isCollapsedGroup: colCollapsed,
        }) => {
          if (rowCollapsed && colCollapsed) return;
          if (rowCollapsed && colSub !== null) return;
          const cell = getCell(index, colPrimary, colSub, rowPrimary, rowSub);
          if (cell) values.push(cell.value);
        },
      );
    },
  );

  return values;
}

/**
 * Aggregate all values for a scope.
 * Explicitly handles 4 modes:
 * - fully specified
 * - partial (1 axis expanded)
 * - collapsed (1 axis total)
 * - grand total
 */
export function aggregateForScope(
  params: AggregateForScopeParams,
  aggregateFn?: (values: BreakdownCell['value'][]) => BreakdownCell['value'],
): BreakdownCell['value'] {
  const { index, row, column, subRow, subColumn } = params;

  // Case A: fully specified (exact cell)
  // Check if all dimensions are explicitly specified (including null for sub-dimensions)
  if (row && column && subRow && subColumn) {
    const cell = getCell(index, column, subColumn, row, subRow);
    return cell ? cell.value : 0;
  }

  // Other cases: resolve all applicable cells via getValuesForScope
  const values = getValuesForScope(params);

  if (aggregateFn) return aggregateFn(values);

  // Default aggregation: numeric sum
  return values.map(extractNumericValue).reduce((sum, v) => sum + v, 0);
}
